import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { T, TF } from "react-translator-component";
import appServices from "../../services/app-services/appServices";
import { isSubLeaderRole, isGroupLeader, convertUserImage } from "../../util/utils";
import { adjustUserInfo } from "../../util/MultiMenu";
import LoadingModal from "../../components/LoadingModal";
import GCBackButton from "../../components/GreenCity/GCBackButton";
import TableComponent from "../../components/GreenCity/TableComponent";
import LeaveGroupConfirmation from "../../components/Models/GreenCity/LeaveGroupConfirmation";
import MessageModal from "../../components/Models/GreenCity/MessageModal";
import { ErrorMessageModal } from "../../components/ErrorMessageModal";
import SubmitButton from "../../components/SubmitButton";
import MultiMenu from "../../components/MultiMenu";
import profile from "../../assets/images/profile_img.png";
import { Avatar, Box, Typography } from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { styles } from "../styles";
import dayjs from "../../util/dayjs"

interface EventDetail {
  address: string;
  application_cancellation_rule: string;
  application_end_date: string;
  application_start_date: string;
  description: string;
  end_date: string;
  id: number;
  participant_settings: {
    id: number;
    name: string;
    number_of_participants: number | null;
    fee_amount: string | null;
    free: boolean;
  }[];
  post_id: number;
  postal_code: string;
  start_date: string;
  title: string;
  url: string;
  users: {
    id: number;
    name: string;
    status: string;
    role: null;
    application_details: null;
  }[];
  venue_name: string;
  voting_question: string;
}
interface ApplicationDetails {
  id: number;
  name: string;
  free: boolean;
  fee_amount: string;
  number_of_participants: number;
  total_amount: number;
}

interface User {
  id: number;
  name: string;
  status: string;
  role: string | null;
  application_details: string; // JSON string
}

const EventUsers = () => {
  const defaultImage = require("../../assets/images/default.png");
  const today = dayjs().startOf("day")

  const navigate = useNavigate();
  const location = useLocation();
  const { event } = location.state || {};
  const [eventDetail, setEventDetail] = useState<EventDetail>({
    address: "",
    application_cancellation_rule: "",
    application_end_date: "",
    application_start_date: "",
    description: "",
    end_date: "",
    id: 0,
    participant_settings: [],
    post_id: 0,
    postal_code: "",
    start_date: "",
    title: "",
    url: "",
    users: [],
    venue_name: "",
    voting_question: "",
  });
  const { user } = useSelector((state: any) => state.userAuth);
  const groupId = useLocation().state.groupId

  const [groupInfo, setGroupInfo] = useState<any>([]);
  const [groupIcon, setGroupIcon] = useState<File>();
  const [loading, setLoading] = useState<boolean>(false);
  const [allJoiningUsers, setAllJoiningUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [openPaymentModal, setOpenPaymentModal] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>('');
  const [openMessageModal, setOpenMessageModal] = useState<boolean>(false);
  const [openErrorMessageModal, setOpenErrorMessageModal] = useState<boolean>(false);
  const [cancelPaymentUser, setCancelPaymentUser] = useState<any>();

  const isLeadersRole = () => {
    return isSubLeaderRole(groupInfo?.role) || isGroupLeader(groupInfo, user)
  }

  const getGroupInfo = () => {
    appServices.getMembersGroup(groupId).then(res => {
      const groupInfoRes = res.member_group
      setGroupInfo(groupInfoRes)
    })
  }
  const getGroupIcon = () => {
    appServices.getGroupIcon(groupId).then(blob => {
      const file = new File([blob], groupInfo.group_icon, { type: blob.type });
      setGroupIcon(file);
    }).catch(() => {
      console.error("Failed to get group icon image")
    });
  }

  const getEventDetails = async () => {
    try {
      setLoading(true);

      const res = await appServices.getSingleEvent(event?.eventId);
      setEventDetail(res?.post_event);

      const eventDate = dayjs(event.eventEndDate).startOf("day");
      const postEventUsers = res?.post_event?.users

      if (eventDate.isBefore(today) || event.status === "Cancelled") {
        setAllJoiningUsers(postEventUsers);
        setFilteredUsers(postEventUsers);
      } else {
        const approvedPostEventUsers = postEventUsers?.filter(
          (user: any) => user.status !== "Cancelled"
        )
        setAllJoiningUsers(approvedPostEventUsers);
        setFilteredUsers(approvedPostEventUsers);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  const filterUsersByCategory = (category: string) => {
    const filtered = allJoiningUsers?.filter((user: User) => {
      let appDetails: ApplicationDetails[] = [];

      try {
        const rawDetails = user.application_details;

        // Check if data is a string (meaning it's in Ruby-style format)
        if (typeof rawDetails === "string") {
          const fixedJsonString = rawDetails.replace(/(\w+)=>/g, '"$1":'); // Convert Ruby-style JSON
          appDetails = JSON.parse(fixedJsonString);
        } else if (Array.isArray(rawDetails)) {
          appDetails = rawDetails; // Already valid JSON
        }
      } catch (error) {
        console.error("Error parsing application_details:", error);
        return false; // Skip users with invalid JSON
      }

      return appDetails.some(
        (detail) =>
          detail.name === category && detail.number_of_participants > 0
      );
    });

    setFilteredUsers(filtered);
  };

  const countNumberOfParticipantsByCategory = () => {
    let numberOfParticipantArray: number[] = []
    filteredUsers.map((user: any) => {
      if (selectedIndex !== null) {
        numberOfParticipantArray.push(user?.application_details[selectedIndex]?.number_of_participants)
      } else {
        user?.application_details.map((eventDetail: any) => {
          numberOfParticipantArray.push(eventDetail?.number_of_participants)
        })
      }
    })
    return numberOfParticipantArray.reduce((prevValue, currentValue) => prevValue + currentValue, 0)
  }

  const handleClick = (index: number) => {
    setSelectedIndex(index);
  };

  const changeUserStatus = async (user: any) => {
    const eventEndDate = dayjs(eventDetail?.end_date).startOf("day"); // Convert and normalize the event date
    if (eventEndDate.isBefore(today) || event.status === "Cancelled") {
      return false;
    }
    const body = {
      status: "confirmed",
    };

    try {
      await appServices.updatePostEventUsers(user?.id, body);

      // Update the local `users` state directly
      const updatedUsers = filteredUsers?.map((u: User) =>
        u.id === user.id ? { ...u, status: "confirmed" } : u
      );

      setFilteredUsers(updatedUsers);
      return true
    } catch (error: any) {
      console.error(error);
      setModalMessage(error?.response?.data?.message)
      setOpenErrorMessageModal(true)
      return false
    }
  };

  useEffect(() => {
    getGroupInfo();
    getGroupIcon();
    getEventDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const altUserImage = (image: any) => {
    return image ? convertUserImage(image) : profile
  }

  const isCancelPendingUser = (userId: number) => {
    return filteredUsers.some((user: any) => user.id === userId && user.status === "Cancel-pending")
  }

  const repayUser = async () => {
    try {
      setOpenPaymentModal(false);
      setLoading(true);
      await appServices.refundPostEventUsers(cancelPaymentUser?.id);
      setSelectedIndex(null);
      getEventDetails();
      setLoading(false);
      setModalMessage(T("event_users_refunded"));
      setOpenMessageModal(true);
      return true
    } catch (error: any) {
      setLoading(false);
      const errorResponse = error?.response?.data?.message
      setModalMessage(
        typeof errorResponse === "object" ?
        errorResponse.join("\n") :
        errorResponse
      )
      setOpenErrorMessageModal(true);
      console.error(error);
      return false
    }
  };

  const payAmount = () => {
    return cancelPaymentUser?.application_details.reduce((sum: any, item: any) => sum + item.total_amount, 0);
  }

  return (
    <Box>
      <LoadingModal visible={loading} handleClose={() => {}} />
      <MessageModal
        onContinue={() => setOpenMessageModal(false)}
        message={modalMessage}
        open={openMessageModal}
        onClose={() => setOpenMessageModal(false)}
      />
      <ErrorMessageModal
        showButton
        messages={[modalMessage]}
        open={openErrorMessageModal}
        onClose={() => setOpenErrorMessageModal(false)}
        alignLeft={modalMessage.indexOf("\n") !== -1}
      />

      <LeaveGroupConfirmation
        open={openPaymentModal}
        onClose={() => setOpenPaymentModal(false)}
        onContinue={() => repayUser()}
        headerText={TF("{0}:\n{1}", T("group_name"), groupInfo?.name)}
        message1={TF("{0}:\n{1}", T("event_name"), eventDetail?.title)}
        message2={TF("{0}\n{1}", T("event_users_event_users"), cancelPaymentUser?.nickname)}
        message3={TF(T("event_users_amount_sum"), payAmount())}
        showCheckBoxText={T("event_users_confirm_refunding")}
      />

      <Box style={style.headBox}>
        <GCBackButton onClick={() => navigate(-1)} />
        <Box sx={{ display: "flex", gap: 1, marginTop: 1 }}>
          <Typography sx={{ fontWeight: "700" }}>
            <img
              src={groupIcon instanceof File ? URL.createObjectURL(groupIcon) : defaultImage}
              alt={"img"}
              style={{
                ...styles.farmProfileIcon,
                height: 50,
                width: 50,
                margin: 0,
              }}
            />
            &nbsp;{groupInfo?.name}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ padding: "0 20px" }}>
        <Box sx={{ marginTop: 4 }}>
          <TableComponent
            data={[
              {
                col1: T("event_name"),
                col2: event?.eventTitle,
              },
              {
                col1: T("number_of_participants"),
                col2: countNumberOfParticipantsByCategory(),
              },
            ]}
          />
        </Box>
        {eventDetail?.participant_settings?.length > 0 && (
          <Box sx={style.ApplicationFilterBox}>
            <Typography sx={{ fontWeight: "bold" }}>{T("participant_settings")}</Typography>
            <Box sx={{ marginTop: 2 }}>
              {eventDetail?.participant_settings?.map(
                (item: any, index: number) => (
                  <Box
                    sx={{
                      ...style.ApplicationFilter,
                      backgroundColor:
                        selectedIndex === index ? "#90C660" : "white",
                    }}
                    key={index}
                    onClick={() => {
                      filterUsersByCategory(item?.name);
                      handleClick(index);
                    }}
                  >
                    <Typography sx={{}}>
                      {TF("{0}{1}", item?.name, T("colon"))}
                      {TF("{0}{1}{2}", item?.number_of_participants, T("event_users_participant_settings_persons"), T("colon"))}
                      {item?.free ? T("free") : TF("{0}{1}",Number(item?.fee_amount), T("unit_yen"))}
                    </Typography>
                  </Box>
                )
              )}
            </Box>
          </Box>
        )}
        <Box
          sx={{
            padding: "20px 0px",
            marginTop: 1,
          }}
        >
          <p style={{ fontSize: 14, fontWeight: 600 }}>
            <GroupIcon /> {T("member_list")}
          </p>
          {filteredUsers?.map((targetUser: any, index: number) => {
            return (
              <Box key={index}>
                <Box sx={style.UserBox}>
                  <Typography>{index + 1}</Typography>
                  <Box sx={style.UserDetail}>
                    <Avatar
                      alt="Remy Sharp"
                      sx={{ width: 30, height: 30 }}
                      src={altUserImage(targetUser.image)}
                    />
                    <Typography>{targetUser?.nickname}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", marginLeft: "auto" }}>
                    {isCancelPendingUser(targetUser.id) && (
                      <Typography
                        style={{ fontSize: 15, marginRight: '15px', color: 'red', lineHeight: '33px' }}
                        onClick={() => {
                          setOpenPaymentModal(true);
                          setCancelPaymentUser(targetUser)
                        }}
                      >
                        {T("refund_pending")}
                      </Typography>
                    )}
                    <SubmitButton
                      validate={async () => isLeadersRole()}
                      onSubmit={() => changeUserStatus(targetUser)}
                      sx={{padding: 0, minWidth: "auto"}}
                    >
                      <CheckCircleIcon
                        sx={{
                          color: targetUser?.status === "confirmed" ? "green" : "gray",
                          fontSize: "33px",
                        }}
                      />
                    </SubmitButton>
                    <Box
                      sx={{ margin: "0 5px" }}
                      style={style.optionButton}
                    >
                      <MultiMenu
                        targetUser={adjustUserInfo(targetUser, groupInfo)}
                        groupInfo={groupInfo}
                        menuType={"event_user"}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};
const style = {
  optionButton: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "1px",
    height: "30px",
    width: "30px"
  },
  headBox: {
    paddingTop: 100,
    paddingLeft: 16,
    paddingBottom: "12px",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
  },
  BoxIcon: {
    background: "#FF3B00",
    borderRadius: "50%",
    width: "20px",
    height: "20px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: "white",
    padding: 1.5,
  },

  ApplicationFilterBox: {
    padding: "30px 20px",
    backgroundColor: "#F4F4F4",
    marginTop: 4,
    borderRadius: 2,
  },
  ApplicationFilter: {
    marginTop: 2,
    display: "flex",
    gap: 1,
    padding: "12px 10px",
    borderRadius: "5px",
  },
  UserBox: {
    borderBottom: "1px gray solid",
    marginTop: "10px",
    textAlign: "left",
    paddingBottom: "8px",
    fontSize: "15px",
    display: "flex",
    alignItems: "center",
  },
  UserDetail: {
    display: "flex",
    alignItems: "center",
    marginLeft: 2,
    gap: 1,
  },
  MoreOptions: {
    color: "#4792E0",
    cursor: "pointer",
    fontSize: "25px",
  },
};
export default EventUsers;
